import { useState } from 'react';
import { NavButtons } from '../NavButtons';
import { StepPanel } from '../StepPanel';

export interface ReviewField {
  label: string;
  value: string;
}

export interface ReviewRow {
  id: string;
  title: string;
  fields: ReviewField[];
  jumpTo: number;
}

interface Props {
  rows: ReviewRow[];
  onEdit: (index: number) => void;
  onBack: () => void;
  onSubmit: () => void;
  submitting?: boolean;
  declarationAccepted: boolean;
  onDeclarationChange: (accepted: boolean) => void;
}

export const Step9Review = ({
  rows,
  onEdit,
  onBack,
  onSubmit,
  submitting,
  declarationAccepted,
  onDeclarationChange,
}: Props) => {
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(rows.map((r) => [r.id, true])),
  );
  return (
    <StepPanel
      icon="fa-check-circle"
      title="Final Checks"
      description="Please review your details. Click 'Edit' next to any section to make changes before submitting."
    >
      <div>
        {rows.map((r) => (
          <div
            key={r.id}
            className="flex items-start justify-between gap-4 border-b border-ink-50 py-4 last:border-none"
          >
            <button
              type="button"
              onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))}
              className="flex-1 text-left"
            >
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-ink-900">
                <i
                  className={`fas fa-chevron-right mr-2 text-[9px] transition-transform ${
                    open[r.id] ? 'rotate-90' : ''
                  }`}
                />
                {r.title}
              </span>
              {open[r.id] && (
                <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-xs text-ink-700">
                  {r.fields.length === 0 ? (
                    <dd className="col-span-2 italic text-ink-500">(not provided)</dd>
                  ) : (
                    r.fields.map((f, i) => (
                      <div key={i} className="contents">
                        <dt className="font-semibold text-ink-500">{f.label}:</dt>
                        <dd className="text-ink-900">{f.value || '—'}</dd>
                      </div>
                    ))
                  )}
                </dl>
              )}
            </button>
            <button type="button" className="btn-edit" onClick={() => onEdit(r.jumpTo)}>
              Edit
            </button>
          </div>
        ))}
      </div>
      <label className="mt-5 flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={declarationAccepted}
          onChange={(e) => onDeclarationChange(e.target.checked)}
          className="mt-0.5 h-[15px] w-[15px] shrink-0 accent-teal"
        />
        <span className="text-[11px] font-bold uppercase tracking-wider text-ink-700">
          I confirm the information provided is accurate and complete, and I agree to the terms of
          registration.
        </span>
      </label>
      <NavButtons
        onBack={onBack}
        onNext={onSubmit}
        nextDisabled={submitting || !declarationAccepted}
        nextLabel={submitting ? 'Submitting…' : 'Submit Registration ›'}
      />
    </StepPanel>
  );
};
